import axios, { type AxiosInstance, type AxiosResponse } from "axios";
import { t } from "i18next";
import type { Filter, SortField } from "@/models/common";
import type { ResponseQuestion } from "@/models/question";
import type { RootResponse } from "@/models/rootResponse";
import type { SurveyBase, SurveyEdit, SurveyHome } from "@/models/survey";
import type { SurveyPageResponse } from "@/models/surveyPage";
import type { User } from "@/models/users";

type PersistAnswers = {
   success: boolean;
   message: string;
   headers: { entry: string };
   guests?: string[];
};

export type SurveyUpdateData = {
   description?: string;
   end_page_message?: string;
   is_authenticable?: string;
};

export type GetSurveys = {
   page?: number;
   sorts?: SortField[];
   filters: Filter[];
   userId: string;
   optimizedForCount?: boolean;
   value?: string;
   instructions?: Array<{ name: string }>;
};

export type GetSurveyEdit = {
   surveyId: string;
};

export type GetSurveyById = {
   surveyId: string;
   withAnswersView?: boolean;
};

export type DetachNewEntries = {
   surveyId: string;
   userId: string;
};

export type AddUserAccess = {
   userId: string;
   typeId: string;
   creatorId: string;
   surveyId: string;
};

export type UpdateUserAccess = AddUserAccess & {
   shareId: string;
};

export type SearchUserAccesses = {
   search: string;
   surveyId: string;
};

export type UpdateSurveyStatus = {
   surveyId: string;
   statusId: string;
};

export type DeleteSurvey = {
   surveyId: string;
};

export type DuplicateSurvey = {
   surveyId: string;
   title: string;
};

export type PinSurvey = {
   userId: string;
   surveyId: string;
};

type UnpinSurvey = PinSurvey;

export type ValidateAuthentication = {
   surveyId: string;
   entryId: string | null;
   email: string;
   firstname: string;
   lastname: string;
};

export type SendSurveyFromCsv = {
   formData: FormData;
   surveyId: string;
};

export type GetSingleSurveyResponseReport = {
   surveyId: string;
   responseId: string;
};

export type GetSurveyResponsesReport = {
   surveyId: string;
   responseIds: string[];
   isGetAll: boolean;
};

export type BroadcastToMobile = {
   surveyId: string;
   siteIds: string[];
   isMultipleAnswers: boolean;
};

export type SendParticipantCorrection = {
   surveyId: string;
   responseIds: string[];
   isAll: boolean;
};

type SurveyUpdateOrCreateResponse = {
   created: string[];
   updated: string[];
};

type ShareableSyncOperation = {
   operation: string;
   without_detaching: boolean;
   key: string;
   pivot: { is_entry_feedback_recipient: boolean };
};

function buildMutateFormData(
   operation: "create" | "update",
   surveyId: string | undefined,
   data: FormData | Record<string, unknown>,
): FormData {
   const formData = new FormData();
   formData.append("mutate[0][operation]", operation);
   if (surveyId) formData.append("mutate[0][key]", surveyId);

   if (data instanceof FormData) {
      data.forEach((value, key) => {
         formData.append(`mutate[0][attributes][${key}]`, value);
      });
   } else {
      Object.entries(data).forEach(([key, value]) => {
         formData.append(
            `mutate[0][attributes][${key}]`,
            value as string | Blob,
         );
      });
   }

   return formData;
}

class SurveyService {
   private provider: AxiosInstance;

   constructor(provider: AxiosInstance) {
      this.provider = provider;
   }

   async getSingleSurveyResponseReport({
      surveyId,
      responseId,
   }: GetSingleSurveyResponseReport): Promise<ArrayBuffer> {
      const response: AxiosResponse<ArrayBuffer> = await this.provider.get(
         `surveys/${surveyId}/entries/${responseId}/download-evaluation-results-pdf`,
         { responseType: "arraybuffer" },
      );
      return response.data;
   }

   async getSurveyResponsesReport({
      surveyId,
      responseIds,
      isGetAll,
   }: GetSurveyResponsesReport): Promise<ArrayBuffer> {
      const response: AxiosResponse<ArrayBuffer> = await this.provider.post(
         `surveys/${surveyId}/entries/download-evaluation-results-pdf`,
         { entries: responseIds, all: isGetAll },
         { responseType: "arraybuffer" },
      );
      return response.data;
   }

   async getSurveyEdit({ surveyId }: GetSurveyEdit): Promise<SurveyEdit> {
      const response: AxiosResponse<RootResponse<SurveyEdit[]>> =
         await this.provider.post(`rest/surveys/search`, {
            search: {
               gates: ["view", "update", "delete"],
               filters: [{ field: "id", operator: "=", value: surveyId }],
               aggregates: [{ relation: "entries", type: "count" }],
               includes: [
                  { relation: "status" },
                  { relation: "type" },
                  { relation: "sites" },
                  { relation: "media" },
                  { relation: "sessions.event" },
                  { relation: "updatedBy" },
                  {
                     relation: "pages",
                     sorts: [{ field: "order", direction: "asc" }],
                     includes: [
                        {
                           relation: "questions",
                           sorts: [{ field: "order", direction: "asc" }],
                           includes: [
                              {
                                 relation: "linkedChoices",
                                 sorts: [{ field: "order", direction: "asc" }],
                                 includes: [
                                    { relation: "media" },
                                    { relation: "comment" },
                                 ],
                              },
                              { relation: "questionType" },
                              { relation: "media" },
                           ],
                        },
                     ],
                  },
               ],
               page: 1,
            },
         });

      return response.data.data[0];
   }

   async getSurveys({
      page,
      sorts,
      filters,
      userId,
      value,
      optimizedForCount = false,
      instructions,
   }: GetSurveys): Promise<RootResponse<SurveyHome[]>> {
      const response: AxiosResponse<RootResponse<SurveyHome[]>> =
         await this.provider.post(`rest/surveys/search`, {
            search: {
               ...(optimizedForCount
                  ? {}
                  : {
                       aggregates: [
                          { relation: "entries", type: "count" },
                          {
                             relation: "pinners",
                             type: "exists",
                             filters: [{ field: "id", value: userId }],
                          },
                       ],
                    }),
               filters,
               text: { value: value ?? "" },
               ...(optimizedForCount
                  ? {}
                  : {
                       includes: [
                          { relation: "sessions.event" },
                          { relation: "updatedBy" },
                          { relation: "type" },
                          {
                             relation: "creator",
                             selects: [
                                { field: "firstname" },
                                { field: "lastname" },
                             ],
                          },
                          {
                             relation: "status",
                             selects: [{ field: "label" }],
                          },
                       ],
                    }),
               selects: optimizedForCount
                  ? [{ field: "id" }]
                  : [
                       { field: "id" },
                       { field: "title" },
                       { field: "created_at" },
                       { field: "updated_at" },
                       { field: "expires_at" },
                       { field: "max_entries" },
                       { field: "has_participant_block" },
                       { field: "survey_type_id" },
                    ],
               ...(sorts?.length ? { sorts } : {}),
               ...(instructions?.length ? { instructions } : {}),
               page,
               ...(optimizedForCount
                  ? {}
                  : { gates: ["update", "delete", "view"] }),
            },
         });

      return response.data;
   }

   async deleteSurvey({ surveyId }: DeleteSurvey): Promise<void> {
      await this.provider.delete("rest/surveys", {
         data: { resources: [surveyId] },
      });
   }

   async duplicateSurvey({
      surveyId,
      title,
   }: DuplicateSurvey): Promise<SurveyBase> {
      const response: AxiosResponse<SurveyBase> = await this.provider.post(
         `rest/surveys/actions/duplicate`,
         {
            fields: [
               { name: "survey_id", value: surveyId },
               { name: "title", value: title },
            ],
         },
      );

      return response.data;
   }

   async createSurvey(
      data: FormData | Record<string, unknown>,
   ): Promise<SurveyBase["id"]> {
      const formData = buildMutateFormData("create", undefined, data);
      const response: AxiosResponse<{ created: string[] }> =
         await this.provider.post(`rest/surveys/mutate`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
         });

      return response.data.created[0];
   }

   async updateEndPage(
      data: FormData | Record<string, unknown>,
      surveyId: string,
   ): Promise<SurveyBase["id"]> {
      const formData = buildMutateFormData("update", surveyId, data);
      const response: AxiosResponse<{ updated: string[] }> =
         await this.provider.post(`rest/surveys/mutate`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
         });

      return response.data.updated[0];
   }

   async updateIntroPage(data: FormData): Promise<SurveyBase["id"]> {
      const response: AxiosResponse<{ updated: string[] }> =
         await this.provider.post(`rest/surveys/mutate`, data, {
            headers: { "Content-Type": "multipart/form-data" },
         });
      return response.data.updated[0];
   }

   async updateSurvey({
      body,
      surveyId,
   }: {
      body: FormData | Record<string, unknown>;
      surveyId: string;
   }): Promise<SurveyUpdateOrCreateResponse> {
      const formData = buildMutateFormData("update", surveyId, body);
      const response: AxiosResponse<SurveyUpdateOrCreateResponse> =
         await this.provider.post(`rest/surveys/mutate`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
         });

      return response.data;
   }

   async updateSurveyStatus({
      surveyId,
      statusId,
   }: UpdateSurveyStatus): Promise<SurveyUpdateOrCreateResponse> {
      const response: AxiosResponse<SurveyUpdateOrCreateResponse> =
         await this.provider.post(`rest/surveys/mutate`, {
            mutate: [
               {
                  operation: "update",
                  key: surveyId,
                  attributes: { survey_status_id: statusId },
               },
            ],
         });
      return response.data;
   }

   async addUserAccess({
      userId,
      typeId,
      creatorId,
      surveyId,
   }: AddUserAccess): Promise<SurveyBase> {
      const response: AxiosResponse<RootResponse<SurveyBase>> =
         await this.provider.post(`rest/surveys/mutate`, {
            mutate: [
               {
                  operation: "update",
                  key: surveyId,
                  relations: {
                     shares: [
                        {
                           operation: "create",
                           relations: {
                              shareablePermission: {
                                 operation: "attach",
                                 key: typeId,
                              },
                              creator: { operation: "attach", key: creatorId },
                              user: { operation: "attach", key: userId },
                           },
                        },
                     ],
                  },
               },
            ],
         });

      return response.data.data;
   }

   async updateUserAccess({
      userId,
      typeId,
      creatorId,
      surveyId,
      shareId,
   }: UpdateUserAccess): Promise<SurveyBase> {
      const response: AxiosResponse<RootResponse<SurveyBase>> =
         await this.provider.post(`rest/surveys/mutate`, {
            mutate: [
               {
                  operation: "update",
                  key: surveyId,
                  relations: {
                     shares: [
                        {
                           operation: "update",
                           key: shareId,
                           relations: {
                              shareablePermission: {
                                 operation: "attach",
                                 key: typeId,
                              },
                              creator: { operation: "attach", key: creatorId },
                              user: { operation: "attach", key: userId },
                           },
                        },
                     ],
                  },
               },
            ],
         });

      return response.data.data;
   }

   async pinSurvey({ userId, surveyId }: PinSurvey): Promise<void> {
      const formData = new FormData();
      formData.append("resources[0]", userId);

      await this.provider.post(`/surveys/${surveyId}/users/attach`, formData, {
         headers: { "Content-Type": "multipart/form-data" },
      });
   }

   async unpinSurvey({ userId, surveyId }: UnpinSurvey): Promise<void> {
      await this.provider.delete(`/surveys/${surveyId}/users/detach`, {
         headers: { "Content-Type": "application/json" },
         data: JSON.stringify({ resources: [userId] }),
      });
   }

   async searchUserAccesses({
      search,
      surveyId,
   }: SearchUserAccesses): Promise<RootResponse<User[]>> {
      const response: AxiosResponse<RootResponse<User[]>> =
         await this.provider.post(`/surveys/${surveyId}/shareables/search`, {
            includes: [{ relation: "pivot.shareablePermission" }],
            sort: [{ field: "firstname", direction: "desc" }],
            search: { value: `%${search}%` },
         });
      return response.data;
   }

   async downloadImportModel(): Promise<Blob> {
      const response = await this.provider.get(
         `rest/surveys/model-import-participants`,
         { responseType: "blob" },
      );
      return response.data;
   }

   async persistAnswers(body: unknown): Promise<PersistAnswers[]> {
      const response = await this.provider.post(
         `/entries/collecting-data-from-survey`,
         body,
         { headers: { "Content-Type": "multipart/form-data" } },
      );

      const results: PersistAnswers[] = response.data;
      if (!results[0].success) {
         throw new Error(results[0].message);
      }

      return results;
   }

   async createPage(form: FormData): Promise<SurveyPageResponse> {
      const surveyId = form.get("survey_id")?.toString() ?? "";
      const label = form.get("label")?.toString() ?? "";
      const headerText = form.get("header_text")?.toString() ?? "";
      const pageOrder = form.get("page_order");

      const response: AxiosResponse<SurveyPageResponse> =
         await this.provider.post(
            `rest/surveys/mutate`,
            {
               mutate: [
                  {
                     operation: "update",
                     key: surveyId,
                     relations: {
                        pages: [
                           {
                              operation: "create",
                              attributes: {
                                 label,
                                 header_text: headerText,
                                 order: pageOrder,
                              },
                           },
                        ],
                     },
                  },
               ],
            },
            { headers: { "Content-Type": "application/json" } },
         );
      return response.data;
   }

   async detachNewEntriesBySurveyId({
      surveyId,
      userId,
   }: DetachNewEntries): Promise<void> {
      await this.provider.post(
         `/users/${userId}/surveys/${surveyId}/read-new-entries`,
      );
   }

   async validateAuthentication({
      surveyId,
      entryId,
      email,
      firstname,
      lastname,
   }: ValidateAuthentication): Promise<{ valid: boolean; message?: string }> {
      try {
         await this.provider.post(`/entries/validate-authentication`, {
            survey_id: surveyId,
            ...(entryId ? { entry_id: entryId } : {}),
            email,
            firstname,
            lastname,
         });
         return { valid: true };
      } catch (error: unknown) {
         let message = t("defaultError");

         if (axios.isAxiosError(error)) {
            const data = error.response?.data as
               | { message?: string; errors?: Record<string, string[]> }
               | undefined;

            message =
               data?.message ??
               data?.errors?.email?.[0] ??
               error.message ??
               message;
         } else if (error instanceof Error) {
            message = error.message;
         }

         return { valid: false, message };
      }
   }

   async sendSurveyFromCsv({
      surveyId,
      formData,
   }: SendSurveyFromCsv): Promise<RootResponse<string>> {
      const response = await this.provider.post(
         `rest/surveys/${surveyId}/import-participants`,
         formData,
         { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
   }

   async resendEmail(entryId: string): Promise<void> {
      await this.provider.post(`/entries/${entryId}/guests/sendMailRegister`);
   }

   async broadcastToMobile({
      surveyId,
      siteIds,
      isMultipleAnswers,
   }: BroadcastToMobile): Promise<void> {
      await this.provider.post("/rest/surveys/actions/mobile-broadcast", {
         fields: [
            { name: "survey_id", value: surveyId },
            { name: "site_ids", value: siteIds },
            { name: "is_multiple_answers", value: isMultipleAnswers },
         ],
      });
   }

   async sendNotificationEmails(
      surveyId: string,
      isAllowSendPdf: boolean,
      isEmailSendNotificationsEnabled: boolean,
   ): Promise<void> {
      await this.provider.patch(`/surveys/${surveyId}`, {
         is_entry_feedback_enabled: isEmailSendNotificationsEnabled,
         is_participant_entry_feedback_enabled: isAllowSendPdf,
      });
   }

   async sendParticipantCorrection({
      surveyId,
      isAll,
      responseIds,
   }: SendParticipantCorrection): Promise<void> {
      await this.provider.post(
         `/rest/surveys/${surveyId}/send-participant-correction`,
         { with_all: isAll, resources: responseIds },
      );
   }

   async updateSurveyNotification(
      surveyId: string,
      resources: Record<string, { is_entry_feedback_recipient: boolean }>,
   ): Promise<void> {
      const shareableSyncOperations: ShareableSyncOperation[] = Object.entries(
         resources,
      ).map(([key, value]) => ({
         operation: "sync",
         without_detaching: true,
         key,
         pivot: {
            is_entry_feedback_recipient: value.is_entry_feedback_recipient,
         },
      }));

      await this.provider.post(`/rest/surveys/mutate`, {
         mutate: [
            {
               operation: "update",
               key: surveyId,
               relations: { shareables: shareableSyncOperations },
            },
         ],
      });
   }

   async getSurveyResponseQuestions({
      surveyId,
      entryId,
   }: {
      surveyId: string;
      entryId: string;
   }): Promise<{ questions: ResponseQuestion[] }> {
      const response: AxiosResponse<
         RootResponse<{ questions: ResponseQuestion[] }[]>
      > = await this.provider.post(`rest/surveys/search`, {
         search: {
            selects: [{ field: "id" }],
            filters: [{ field: "id", value: surveyId }],
            includes: [
               {
                  relation: "questions",
                  sorts: [{ field: "order", direction: "asc" }],
                  scopes: [
                     {
                        name: "withRecursiveAnswers",
                        parameters: [entryId],
                     },
                  ],
                  includes: [{ relation: "questionType" }],
               },
            ],
            page: 1,
         },
      });

      return response.data.data[0];
   }
}

export default SurveyService;
